import { actionToId } from '../core/actions';
import type { BidEvaluation } from './monte-carlo';

function isPoint(evaluation: BidEvaluation): boolean {
  return evaluation.action.action.type === 'bid' && evaluation.action.action.bid === 'points';
}

function bidValue(evaluation: BidEvaluation): number | undefined {
  return evaluation.action.action.type === 'bid' ? evaluation.action.action.value : undefined;
}

function stable(left: BidEvaluation, right: BidEvaluation): number {
  return actionToId(left.action.action).localeCompare(actionToId(right.action.action));
}

/** Compare valued contracts numerically; ties and value-less contracts use stable action IDs. */
function compareBidValue(left: BidEvaluation, right: BidEvaluation): number {
  const leftValue = bidValue(left);
  const rightValue = bidValue(right);
  if (leftValue !== undefined && rightValue !== undefined && leftValue !== rightValue) {
    return leftValue - rightValue;
  }
  return stable(left, right);
}

/** Select the final-seat contract from paired evaluations that clear viability. */
export function selectFinalSeatBid(
  evaluations: readonly BidEvaluation[],
  viabilityThreshold: number,
  tolerance: number,
): { selected: BidEvaluation | undefined; nearEqualActionIds: string[] } {
  const viable = evaluations.filter((evaluation) => evaluation.makeBidRate >= viabilityThreshold);
  const points = viable.filter(isPoint);
  const nonpoints = viable.filter((evaluation) => !isPoint(evaluation));
  if (points.length === 0) {
    const selected = [...nonpoints].sort((left, right) =>
      right.estimatedMatchValue - left.estimatedMatchValue || compareBidValue(left, right),
    )[0];
    return { selected, nearEqualActionIds: [] };
  }
  const bestPoint = Math.max(...points.map((evaluation) => evaluation.estimatedMatchValue));
  const nearPoints = points.filter((evaluation) => bestPoint - evaluation.estimatedMatchValue <= tolerance);
  const smallestPoint = [...nearPoints].sort(compareBidValue)[0]!;
  const bestNonpoint = [...nonpoints].sort((left, right) =>
    right.estimatedMatchValue - left.estimatedMatchValue || compareBidValue(left, right),
  )[0];
  const selected = bestNonpoint && bestNonpoint.estimatedMatchValue > bestPoint
    ? bestNonpoint
    : smallestPoint;
  return {
    selected,
    nearEqualActionIds: selected === smallestPoint
      ? nearPoints.map((evaluation) => actionToId(evaluation.action.action)).sort()
      : [],
  };
}
